// src/components/messaging/MessageSenderModal.jsx
import React, { useState } from 'react';
import { 
  X, MessageCircle, Mail, Send, Users, ChevronDown, 
  Check, Copy, ExternalLink, Loader2, AlertCircle,
  Phone, FileText, Clock
} from 'lucide-react';
import { 
  generateShareLinks, 
  generateMessageContent,
  requestAdminMessage, 
  logMessageSent,
  MessageTemplate 
} from '../../services/notificationService';
import { useAuth } from '../../context/AuthContext';

const MessageSenderModal = ({ isOpen, onClose, report, onMessageSent }) => {
  const { currentUser, userProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('direct');
  const [selectedTemplate, setSelectedTemplate] = useState(MessageTemplate.INITIAL_NOTICE);
  const [selectedChannels, setSelectedChannels] = useState(['whatsapp']);
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  if (!isOpen || !report) return null;

  const templates = [
    { id: MessageTemplate.INITIAL_NOTICE, label: 'Initial Notice', icon: FileText, color: 'text-blue-400' },
    { id: MessageTemplate.REMINDER, label: 'Reminder', icon: Clock, color: 'text-yellow-400' },
    { id: MessageTemplate.FINAL_WARNING, label: 'Final Warning', icon: AlertCircle, color: 'text-red-400' },
    { id: MessageTemplate.SETTLEMENT_REQUEST, label: 'Settlement Request', icon: Users, color: 'text-emerald-400' },
  ];

  const links = generateShareLinks(report, selectedTemplate);
  const content = generateMessageContent(report, selectedTemplate);

  const toggleChannel = (channel) => {
    if (selectedChannels.includes(channel)) {
      if (selectedChannels.length > 1) {
        setSelectedChannels(selectedChannels.filter(c => c !== channel));
      }
    } else {
      setSelectedChannels([...selectedChannels, channel]);
    }
  };

  const handleCopyMessage = async () => {
    try {
      await navigator.clipboard.writeText(content.body);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleDirectSend = async (channel) => {
    try {
      // Log the message
      await logMessageSent(report, selectedTemplate, currentUser.uid, channel);
      
      // Open the appropriate link
      if (channel === 'whatsapp' && links.waLink) {
        window.open(links.waLink, '_blank');
      } else if (channel === 'email' && links.mailLink) {
        window.location.href = links.mailLink;
      } else if (channel === 'sms' && links.smsLink) {
        window.location.href = links.smsLink;
      }
      
      onMessageSent?.();
    } catch (err) {
      setError('Failed to send message');
    }
  };

  const handleAdminRequest = async () => {
    try {
      setSending(true);
      setError(null);
      
      await requestAdminMessage(
        report,
        selectedTemplate,
        currentUser.uid,
        userProfile?.name || 'Unknown',
        selectedChannels
      );
      
      setSuccess('Request submitted! PAYBACK team will send the message within 24 hours.');
      setTimeout(() => {
        onClose();
        onMessageSent?.();
      }, 2000);
    } catch (err) {
      setError('Failed to submit request. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in">
      <div className="bg-slate-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden border border-slate-700 shadow-2xl animate-in slide-in-from-bottom-4">
        
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-700 bg-slate-900">
          <div className="flex items-center gap-3">
            <div className="bg-emerald-500/20 p-2 rounded-lg">
              <Send className="text-emerald-400" size={20} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Send Notification</h2>
              <p className="text-xs text-slate-400">Notify {report.borrowerName} about this report</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="text-slate-400 hover:text-white p-2 hover:bg-slate-700 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-700">
          <button
            onClick={() => setActiveTab('direct')}
            className={`flex-1 py-3 text-sm font-bold transition-colors ${
              activeTab === 'direct' 
                ? 'text-emerald-400 border-b-2 border-emerald-400 bg-emerald-500/5' 
                : 'text-slate-400 hover:text-white'
            }`}
          >
            Send Directly
          </button>
          <button
            onClick={() => setActiveTab('admin')}
            className={`flex-1 py-3 text-sm font-bold transition-colors ${
              activeTab === 'admin' 
                ? 'text-emerald-400 border-b-2 border-emerald-400 bg-emerald-500/5' 
                : 'text-slate-400 hover:text-white'
            }`}
          >
            Request Admin Send
          </button>
        </div>

        <div className="p-4 overflow-y-auto max-h-[calc(90vh-200px)]">
          
          {/* Success/Error Messages */}
          {success && (
            <div className="mb-4 p-3 bg-emerald-500/20 border border-emerald-500/50 rounded-lg text-emerald-400 text-sm flex items-center gap-2 animate-in slide-in-from-top-2">
              <Check size={16} />
              {success}
            </div>
          )}
          
          {error && (
            <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm flex items-center gap-2 animate-in slide-in-from-top-2">
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          {/* Template Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-300 mb-3">Message Template</label>
            <div className="grid grid-cols-2 gap-2">
              {templates.map((template) => {
                const Icon = template.icon;
                return (
                  <button
                    key={template.id}
                    onClick={() => setSelectedTemplate(template.id)}
                    className={`p-3 rounded-lg border text-left transition-all ${
                      selectedTemplate === template.id
                        ? 'bg-slate-700 border-emerald-500 ring-1 ring-emerald-500'
                        : 'bg-slate-900 border-slate-700 hover:border-slate-600'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Icon size={16} className={template.color} />
                      <span className={`text-sm font-medium ${
                        selectedTemplate === template.id ? 'text-white' : 'text-slate-300'
                      }`}>
                        {template.label}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Recipient Info */}
          <div className="mb-6 bg-slate-900/50 rounded-lg p-4 border border-slate-700/50">
            <h4 className="text-xs font-bold text-slate-400 uppercase mb-3">Recipient</h4>
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <Phone size={14} className="text-slate-500" />
                <span className="text-sm text-white font-mono">{report.borrowerMobile}</span>
                {report.borrowerMobile && (
                  <span className="text-xs bg-green-900/30 text-green-400 px-2 py-0.5 rounded">WhatsApp Ready</span>
                )}
              </div>
              {report.borrowerEmail && (
                <div className="flex items-center gap-3">
                  <Mail size={14} className="text-slate-500" />
                  <span className="text-sm text-white">{report.borrowerEmail}</span>
                </div>
              )}
            </div>
          </div>

          {/* Message Preview */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-slate-300">Message Preview</label>
              <button
                onClick={handleCopyMessage}
                className="flex items-center gap-1 text-xs text-slate-400 hover:text-emerald-400 transition-colors"
              >
                {copied ? <Check size={12} /> : <Copy size={12} />}
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <div className="bg-slate-900 rounded-lg p-4 border border-slate-700 max-h-48 overflow-y-auto">
              <p className="text-xs text-slate-400 mb-2 font-bold">{content.subject}</p>
              <p className="text-sm text-slate-300 whitespace-pre-wrap font-mono leading-relaxed">
                {content.body}
              </p>
            </div>
          </div>

          {/* Direct Send Tab */}
          {activeTab === 'direct' && (
            <div className="space-y-3">
              <p className="text-xs text-slate-400 mb-4">
                Click a button below to open the messaging app with the pre-filled message.
              </p>
              
              <button
                onClick={() => handleDirectSend('whatsapp')}
                className="w-full flex items-center justify-center gap-3 bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold py-4 rounded-xl transition-all shadow-lg"
              >
                <MessageCircle size={22} />
                Send via WhatsApp
                <ExternalLink size={14} className="opacity-60" />
              </button>
              
              {report.borrowerEmail ? (
                <button
                  onClick={() => handleDirectSend('email')}
                  className="w-full flex items-center justify-center gap-3 bg-blue-600 hover:bg-blue-500 text-white font-bold py-4 rounded-xl transition-all shadow-lg"
                >
                  <Mail size={22} />
                  Send via Email
                  <ExternalLink size={14} className="opacity-60" />
                </button>
              ) : (
                <div className="w-full flex items-center justify-center gap-3 bg-slate-700 text-slate-500 font-bold py-4 rounded-xl cursor-not-allowed">
                  <Mail size={22} />
                  Email not provided
                </div>
              )}
              
              <button
                onClick={() => handleDirectSend('sms')}
                className="w-full flex items-center justify-center gap-3 bg-slate-700 hover:bg-slate-600 text-white font-bold py-4 rounded-xl transition-all"
              >
                <Phone size={22} />
                Send via SMS
                <ExternalLink size={14} className="opacity-60" />
              </button>
            </div>
          )}

          {/* Admin Request Tab */}
          {activeTab === 'admin' && (
            <div className="space-y-4">
              <div className="bg-blue-900/20 border border-blue-500/30 p-4 rounded-lg">
                <h4 className="text-blue-400 font-bold text-sm mb-2 flex items-center gap-2">
                  <Users size={16} />
                  Request PAYBACK to Send
                </h4>
                <p className="text-xs text-slate-400">
                  Our team will send the notification on your behalf within 24 hours. 
                  Messages sent by PAYBACK carry official platform authority.
                </p>
              </div>

              {/* Channel Selection */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-3">
                  Send Via (select one or more)
                </label>
                <div className="flex gap-3">
                  <button
                    onClick={() => toggleChannel('whatsapp')}
                    className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-lg border transition-all ${
                      selectedChannels.includes('whatsapp')
                        ? 'bg-green-900/30 border-green-500 text-green-400'
                        : 'bg-slate-900 border-slate-700 text-slate-400 hover:border-slate-600'
                    }`}
                  >
                    <MessageCircle size={18} />
                    <span className="font-medium">WhatsApp</span>
                    {selectedChannels.includes('whatsapp') && <Check size={14} />}
                  </button>
                  
                  <button
                    onClick={() => toggleChannel('email')}
                    disabled={!report.borrowerEmail}
                    className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-lg border transition-all ${
                      selectedChannels.includes('email')
                        ? 'bg-blue-900/30 border-blue-500 text-blue-400'
                        : report.borrowerEmail
                          ? 'bg-slate-900 border-slate-700 text-slate-400 hover:border-slate-600'
                          : 'bg-slate-900 border-slate-800 text-slate-600 cursor-not-allowed'
                    }`}
                  >
                    <Mail size={18} />
                    <span className="font-medium">Email</span>
                    {selectedChannels.includes('email') && <Check size={14} />}
                  </button>
                </div>
              </div>

              <button
                onClick={handleAdminRequest}
                disabled={sending || selectedChannels.length === 0}
                className="w-full flex items-center justify-center gap-3 bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-4 rounded-xl transition-all shadow-lg shadow-emerald-900/20 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sending ? (
                  <>
                    <Loader2 size={20} className="animate-spin" />
                    Submitting Request...
                  </>
                ) : (
                  <>
                    <Send size={20} />
                    Request Admin to Send
                  </>
                )}
              </button>
              
              <p className="text-center text-xs text-slate-500">
                Free for verified lenders • 24-hour delivery guarantee
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MessageSenderModal;